import { Box, Skeleton, Typography } from '@compass/core';
import { PieChart, Export, Series, Label, Connector, Legend, Title, Font } from 'devextreme-react/pie-chart';
import { useParticipantsData } from '../contexts/ParticipantsDataContext';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';
import { ParticipantsFilterControls } from './ParticipantsFilterControls';
import { ParticipantStatusChartValue } from '../types/ParticipantStatusData';
import { useRef, useMemo, useContext } from 'react';

interface ChartLabelArg {
    value: number;
    point?: {
        data?: ParticipantStatusChartValue;
    };
}

const ParticipantsChartContent = ({ chartRef }: { chartRef: React.RefObject<PieChart> }) => {
    const { chartData, loading, error, data, filter, programs } = useParticipantsData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        labelSize: 14,
        legendSize: 14
    } : {
        labelSize: 12,
        legendSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearLabel = filter.schoolYear ? `${filter.schoolYear}-${filter.schoolYear + 1}` : 'All Years';
        const programLabel = filter.programId 
            ? programs.find(p => p.id === filter.programId)?.name || 'Unknown Program'
            : 'All Programs';
        const exportDate = new Date().toLocaleDateString();
        
        return {
            title: 'Participants Assisted',
            subtitle: `Year: ${yearLabel} | Program: ${programLabel} | Exported: ${exportDate}`
        };
    }, [filter.schoolYear, filter.programId, programs]);

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!chartData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography variant="body2" color="text.secondary">
                    No participants found for selected filters
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <ParticipantsFilterControls />
            
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <PieChart
                    ref={chartRef}
                    id="participants-pie-chart"
                    dataSource={chartData}
                    palette="Bright"
                    width="100%"
                    height="100%"
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series argumentField="label" valueField="value">
                        <Label 
                            visible={true}
                            customizeText={(arg: ChartLabelArg) => {
                                const count = arg.point?.data?.count ?? 0;
                                return `${count} (${arg.value.toFixed(1)}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend
                        visible={true}
                        horizontalAlignment="center"
                        verticalAlignment="bottom"
                    >
                        <Font size={styleConfig.legendSize} />
                    </Legend>
                    <Export enabled={true} />
                </PieChart>
            </Box>
            
            {data && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Total Participants: {data.totalParticipants}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export const ParticipantsChartCard = () => {
    const { filter, programs } = useParticipantsData();
    const chartRef = useRef<PieChart>(null);

    const handleExport = () => {
        if (chartRef.current) {
            const yearLabel = filter.schoolYear || 'AllYears';
            const programName = filter.programId 
                ? programs.find(p => p.id === filter.programId)?.name?.replace(/\s+/g, '_') || 'Program'
                : 'AllPrograms';
            chartRef.current.instance.exportTo(`Participants_${yearLabel}_${programName}`, 'png');
        }
    };

    return (
        <DashboardCard title="Participants" onExport={handleExport}>
            <ParticipantsChartContent chartRef={chartRef} />
        </DashboardCard>
    );
};